import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { FileModel, FolderModel } from '../../../../../../../typings/api/skymap/rest/v1/.common';
import { Button } from '../../../button/button';
import { Dialog } from '../../../dialog/dialog';
import { Stack } from '../../../stack/stack';
import { FileManager } from '../../file-manager';

interface Props {
  selectionMode:
    | 'CheckFiles'
    | 'CheckFolders'
    | 'CheckFoldersAndFiles'
    | 'SingleFolder'
    | 'SingleFile';
  onCancel: () => void;
  onSelect: (
    folders: FolderModel[],
    files: FileModel[],
    currentFolderId?: string,
  ) => Promise<void> | void;
}

const FileManagerSelectionDialog = (props: Props) => {
  const { t } = useTranslation();
  const [checkedFolders, setCheckedFolders] = React.useState<FolderModel[]>([]);
  const [checkedFiles, setCheckedFiles] = React.useState<FileModel[]>([]);
  const [currentFolderId, setCurrentFolderId] = React.useState<string>();

  const foldersCheckable =
    props.selectionMode === 'CheckFoldersAndFiles' || props.selectionMode === 'CheckFolders';
  const filesCheckable =
    props.selectionMode === 'CheckFoldersAndFiles' ||
    props.selectionMode === 'CheckFiles' ||
    props.selectionMode === 'SingleFile';

  const content = React.useMemo(() => {
    return (
      <FileManager
        filesCheckable={filesCheckable}
        foldersCheckable={foldersCheckable}
        maxFileSelection={props.selectionMode === 'SingleFile' ? 1 : undefined}
        onCheckedFilesChanged={setCheckedFiles}
        onCheckedFoldersChanged={setCheckedFolders}
        onCurrentFolderIdChanged={setCurrentFolderId}
      />
    );
  }, [filesCheckable, foldersCheckable, props.selectionMode]);

  return (
    <Dialog
      closeOnDimmerClick={true}
      contentPadding={false}
      height={'75%'}
      width={1200}
      onClose={props.onCancel}
    >
      {{
        header:
          props.selectionMode === 'SingleFolder'
            ? t('fileManager.fileManagerSelectionDialog.titleSingleFolder', { ns: 'components' })
            : props.selectionMode === 'CheckFoldersAndFiles'
              ? t('fileManager.fileManagerSelectionDialog.titleCheckFoldersAndFiles', {
                  ns: 'components',
                })
              : props.selectionMode === 'CheckFolders'
                ? t('fileManager.fileManagerSelectionDialog.titleCheckFolders', {
                    ns: 'components',
                  })
                : t('fileManager.fileManagerSelectionDialog.titleCheckFiles', { ns: 'components' }),
        content,
        footer: {
          left: (
            <Stack justifyContent="center" spacing={0.25}>
              {foldersCheckable && (
                <span>
                  <Trans
                    components={{ bold: <strong /> }}
                    i18nKey="fileManager.fileManagerSelectionDialog.selectedFolders"
                    ns="components"
                    values={{ count: checkedFolders.length }}
                  />
                </span>
              )}
              {filesCheckable && (
                <span>
                  <Trans
                    components={{ bold: <strong /> }}
                    i18nKey="fileManager.fileManagerSelectionDialog.selectedFiles"
                    ns="components"
                    values={{ count: checkedFiles.length }}
                  />
                </span>
              )}
            </Stack>
          ),
          right: (
            <>
              <Button
                color="primary"
                disabled={
                  props.selectionMode !== 'SingleFolder' &&
                  checkedFolders.length + checkedFiles.length === 0
                }
                variant="contained"
                onClick={async () => {
                  await props.onSelect(checkedFolders, checkedFiles, currentFolderId);
                }}
              >
                {t('choose', { ns: 'common' })}
              </Button>
              <Button variant="text" onClick={props.onCancel}>
                {t('cancel', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

export { FileManagerSelectionDialog, Props as FileManagerSelectionDialogProps };
